import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  afterNextRender,
  signal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeferModule } from 'primeng/defer';
import { BehaviorSubject, delay, filter, first, firstValueFrom, lastValueFrom, pipe } from 'rxjs';
import { SwiperOptions } from 'swiper/types';

import { AssetType, CustomSlidersParams, ENVIRONMENT, Environment } from '@environment';
import { FeatureMoviesCarouselComponent } from '@usheru-hff/custom-page/feature-movies-carousel';
import { Competition, CompetitionsService } from '@usheru-hff/movie-detail/feature-competitions';
import { FeatureFilmInteractionsComponent } from '@usheru-hff/movie-detail/feature-film-interactions';
import { FeatureGalleryCarouselComponent } from '@usheru-hff/movie-detail/feature-gallery-carousel';
import { FeatureLaurelsCarouselComponent } from '@usheru-hff/movie-detail/feature-laurels-carousel';
import { FeatureMovieHeroComponent } from '@usheru-hff/movie-detail/feature-movie-hero';
import { MovieDetailFeatureShowtimesStreamingModule } from '@usheru-hff/movie-detail/feature-showtimes-streaming';
import { FeatureVideosCarouselComponent } from '@usheru-hff/movie-detail/feature-videos-carousel';
import { MovieDetailUiMovieElementsModule } from '@usheru-hff/movie-detail/ui-movie-elements';
import { NewsFeatureNewsCarouselModule } from '@usheru-hff/news/feature-news-carousel';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import {
  Channel,
  Company,
  existsMovieTag,
  getMovieGenres,
  getMovieTags,
  Movie,
  MovieLaurel,
  PageType,
  TabType
} from '@usheru-hff/shared/domain';
import { SharedFeatureVideoThumbnailModule, VideoThumbnailComponent } from '@usheru-hff/shared/feature-video-thumbnail';
import { FeatureFilmInteractionsToken } from '@usheru-hff/shared/tokens';
import { LoadingIndicatorComponent } from '@usheru-hff/shared/ui-loading-indicator';
import { SharedUiPictureModule } from '@usheru-hff/shared/ui-picture';
import { SwiperDirective } from '@usheru-hff/shared/ui-swiper';
import { SharedUtilsModule } from '@usheru-hff/shared/utils';
import { EnvironmentService } from '@usheru-hff/shared/utils-environment';
import { ImageService, SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { TitleService } from '@usheru-hff/shared/utils-title';
import { SharedUtilsTranslationModule, TranslationsService } from '@usheru-hff/shared/utils-translation';
import { SkeletonLoaderComponent } from '@usheru-hff/ui/skeleton';
import { UplayerService } from '@usheru-hff/uplayer/domain';

@Component({
  standalone: true,
  selector: 'ush-movie-detail-page',
  templateUrl: './movie-detail-page.component.html',
  styleUrls: ['./movie-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SharedFeatureVideoThumbnailModule,
    SharedUtilsImageModule,
    SharedUtilsTranslationModule,
    MovieDetailUiMovieElementsModule,
    MovieDetailFeatureShowtimesStreamingModule,
    SharedUiPictureModule,
    LoadingIndicatorComponent,
    SharedUtilsModule,
    DeferModule,
    FeatureMovieHeroComponent,
    SwiperDirective,
    FeatureGalleryCarouselComponent,
    FeatureVideosCarouselComponent,
    FeatureMoviesCarouselComponent,
    FeatureLaurelsCarouselComponent,
    NewsFeatureNewsCarouselModule,
    SkeletonLoaderComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MovieDetailPageComponent implements OnInit, OnDestroy, AfterViewInit {
  /* This input is filled up by the resolver */
  @Input() movie: Movie;
  @Input() isPartialMovieLoaded: boolean;
  /**
   * Element reference where show the trailer and other controls.
   */
  @ViewChild('mainVideo', { read: ElementRef }) mainVideoEl: ElementRef;
  @ViewChild('mainVideo') mainVideo: VideoThumbnailComponent;

  /**
   * References to the videos in the gallery section.
   */
  @ViewChildren(VideoThumbnailComponent)
  videosComponent: QueryList<VideoThumbnailComponent>;

  /**
   * //TODO docs
   */
  @ViewChild('uplay') uplaybtn;

  /**
   * @docNotNeccesary
   */
  channel: Channel;

  /**
   * @deprecated Don't found any use
   */
  customSliderParams?: CustomSlidersParams;

  /**
   * @docNotNeccesary
   */
  hasUPlayer: boolean;

  /**
   * Takes the {@link movie} tags in the {@link setTagsClass} and uses as classes selectors.
   */
  movieTagClass: string[] = [];

  /**
   * If it is false show the video with the {@link VideoFullscreenComponent}
   */
  embedVideo: boolean;

  hideShowtimeStreamingTabs: TabType[];

  showWatchNowCtaButton: boolean = false;

  /**
   * Setting from {@link Environment} movieDetailMuteVideo. Applied to the {@link mainVideoEl}.
   */
  mute: boolean;

  /**
   * Setting from {@link Environment} movieDetailAutoplayTrailer. Applied to the {@link mainVideoEl}.
   */
  autoplayTrailer: boolean;

  /**
   * Obervable to know when the {@link mainVideoEl} is ready to be played.
   */
  videoReady = new BehaviorSubject(false);

  /**
   * Scroll under the {@link mainVideoEl} and don't autoplay when the component is initialiced. This is activated with
   * query param with the same name.
   */
  skipTrailer: boolean;

  /**
   * Autoplays the video when the component is initialiced. This is activated with query param with the same name.
   */
  playTrailer: boolean;

  /**
   * Input to the {@link mainVideoEl} that emits when the video should be played.
   */
  triggerPlay: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Carousel setting for the recomendations section.
   */
  recomendationSettings: any;

  /**
   * Used as boolean to activate competition in the {@link mainVideoEl}.
   *
   * //TODO barbeitoalex propostal - change to be a boolean
   */
  competition: Competition;

  /**
   * @deprecated Don't found any use
   */
  showingCompetition = false;

  /**
   * //TODO docs favio
   */
  showtimeStreamingPriority: PageType;

  /**
   * Activates from {@link Environment} movieDetailReviews.
   */
  disableReviews = false;

  /**
   * Activates from {@link Environment} movieDetailNoGallery.
   */
  disableGallery = false;

  /**
   * Activates from {@link Environment} movieDetailNoPosters.
   */
  disablePosters = false;

  /**
   * Activates from {@link Environment} movieDetailNoVideos.
   */
  disableVideos = false;

  /**
   * List of parner companies in the {@link movie}.
   */
  rightOwners: Company[];

  /**
   * Shows {@link rightOwners}. Activates from {@link Environment}
   */
  showRightOwners = false;

  /**
   * Show the shared link inside the {@link VideoThumbnailComponent}
   */
  shareInThumbnail = false;

  /**
   * Alternative template to show {@link movie} date.
   */
  detailContentAlternative = false;

  /**
   * thefestivalagency especial rule.
   */
  movieLaurelsEnabled: boolean;

  /**
   * thefestivalagency especial rule.
   */
  movieLaurels: MovieLaurel[];

  /**
   * thefestivalagency especial rule.
   */
  laurelsResponsiveOptions = [
    {
      breakpoint: 1200,
      byPage: 5
    },
    {
      breakpoint: 1000,
      byPage: 4
    },
    {
      breakpoint: 700,
      byPage: 3
    },
    {
      breakpoint: 450,
      byPage: 2
    }
  ];

  leftLaurelSrc = 'https://cdnstatic.usheru.com/img/misc/left-laurel.png';
  rightLaurelSrc = 'https://cdnstatic.usheru.com/img/misc/right-laurel.png';
  whereToWatch: PageType;
  iconPlay: AssetType;
  portraitPlaceholder;
  landscapePlaceholder;
  movieLandscapeImageBroken: boolean;
  moviePortraitImageBroken: boolean;
  reviewsSwiperConfig: SwiperOptions;
  loading: boolean = true;

  /**
   * Will be set true when the movie have a no-movie-detail tag present.
   */
  noMovieDetailTag: boolean;
  featureFilmInteractionTokenEnabled: boolean;
  filmInteractionComponentRef: ComponentRef<FeatureFilmInteractionsComponent>;

  @ViewChild('filmInteractionComponent', { read: ViewContainerRef }) filmInteractionComponent!: ViewContainerRef;

  constructor(
    private route: ActivatedRoute,
    private envservice: EnvironmentService,
    private trackingService: TrackingService,
    private competitionsService: CompetitionsService,
    private channelService: ChannelService,
    private cd: ChangeDetectorRef,
    @Inject(ENVIRONMENT) private env: Environment,
    @Inject(PLATFORM_ID) private platformId,
    private imgService: ImageService,
    private uplayerService: UplayerService,
    private locationService: LocationService,
    private translationsService: TranslationsService,
    private metaTagsService: MetatagsService,
    private titleService: TitleService,
    private movieService: MovieService,
    @Optional() private featureFilmInteractionToken: FeatureFilmInteractionsToken,
    private location: Location
  ) {
    this.setSwiperConfigs();

    afterNextRender(() => {
      if (this.playTrailer) {
        window.scrollTo(0, 0);

        if (this.videoReady.value) {
          this.triggerPlay.next(true);
        } else {
          this.videoReady.pipe(first(val => val)).subscribe(() => {
            this.triggerPlay.next(true);
          });
        }
      }

      if (this.skipTrailer) {
        const mainVideoEl: HTMLElement = this.mainVideoEl.nativeElement;
        let videoWrapper;
        setTimeout(() => {
          videoWrapper = mainVideoEl.querySelector('.video-thumbnail');
          // check when the component is ready
          if (videoWrapper.clientHeight > 100) {
            window.scrollTo({ top: (videoWrapper as HTMLElement).clientHeight });
          }
        }, 50);
      } else {
        window.scrollTo(0, 0);
      }
    });

    // todo: after laurels have been included in the roles and permision property in channel replace this for
    // for that validation.
    this.movieLaurelsEnabled = env?.customPageParams?.movieDetailLaurelsEnabled;
    this.portraitPlaceholder = this.imgService.portraitPlaceholder;
    this.landscapePlaceholder = this.imgService.landscapePlaceholder;

    // Listen env live changes for testing
    this.envservice.environment$.subscribe(env => {
      this.customSliderParams = env.customSlidersParams;
      this.embedVideo = env?.customPageParams?.movieDetailVideoEmbed;
      this.mute = env?.customPageParams?.movieDetailMuteVideo;
      this.autoplayTrailer = env?.customPageParams?.movieDetailAutoplayTrailer;
      this.shareInThumbnail = env?.customPageParams?.movieDetailShareInThumbnail;
      this.recomendationSettings = env?.customFeatures?.movieDetailRecomendations;
      this.disableReviews = env?.customPageParams?.movieDetailReviews;
      this.disableGallery = env?.customPageParams?.movieDetailNoGallery;
      this.disablePosters = env?.customPageParams?.movieDetailNoPosters;
      this.disableVideos = env?.customPageParams?.movieDetailNoVideos;
      this.showRightOwners = env?.customPageParams?.movieDetailShowRightOwners;
      this.detailContentAlternative = env?.customPageParams?.movieDetailContentAlternative;
      this.hideShowtimeStreamingTabs = env?.hideShowtimeStreamingTabs;
      this.iconPlay = env?.assetsRefs?.videoThumbnailPlayIcon;

      this.showWatchNowCtaButton = env.movieDetail?.hero?.showWatchNowCtaButton || false;

      // todo: after laurels have been included in the roles and permision property in channel replace this for

      // for that validation.

      this.movieLaurelsEnabled = env?.customPageParams?.movieDetailLaurelsEnabled;
    });

    const params = this.route.snapshot.queryParams;
    this.skipTrailer = params?.skipTrailer;
    this.playTrailer = params?.play;
    this.whereToWatch = params?.whereToWatch;
    this.triggerPlay.pipe(filter(val => val)).subscribe(() => {
      this.mainVideo.play();
    });
  }

  /**
   *  @docNotNeccesary
   *
   *  //TODO remove fake movie laurels after this is being testend and in production
   */
  ngOnInit(): void {
    if (this.isPartialMovieLoaded) {
      this.cd.markForCheck();

      firstValueFrom(
        this.movieService.getMovie(
          this.movie.friendlyUrl,
          this.translationsService.currentLanguage().code,
          this.locationService.selectedCountry.slug
        )
      ).then(movie => {
        this.movie = movie;
        this.onMovieFullyLoaded(movie);
        this.loading = false;
        this.cd.markForCheck();
      });
    } else {
      this.onMovieFullyLoaded(this.movie);
      this.loading = false;
      this.cd.markForCheck();
    }
    // todo: change this logic to use a service in order to check if the movie was downloaded already

    this.customSliderParams = this.env.customSlidersParams;
    this.embedVideo = this.env?.customPageParams?.movieDetailVideoEmbed;
    this.mute = this.env?.customPageParams?.movieDetailMuteVideo;
    this.autoplayTrailer = this.env?.customPageParams?.movieDetailAutoplayTrailer;
    this.shareInThumbnail = this.env?.customPageParams?.movieDetailShareInThumbnail;
    this.recomendationSettings = this.env?.customFeatures?.movieDetailRecomendations;
    this.disableReviews = this.env?.customPageParams?.movieDetailReviews;
    this.disableGallery = this.env?.customPageParams?.movieDetailNoGallery;
    this.disablePosters = this.env?.customPageParams?.movieDetailNoPosters;
    this.disableVideos = this.env?.customPageParams?.movieDetailNoVideos;
    this.showRightOwners = this.env?.customPageParams?.movieDetailShowRightOwners;
    this.detailContentAlternative = this.env?.customPageParams?.movieDetailContentAlternative;
    this.channel = this.channelService.channel;
  }

  ngAfterViewInit(): void {
    this.loadCommunityElements();
    this.setMetaTags(this.movie);
    this.goToParamSection();
  }

  // todo: Enable this when the feature is ready
  loadCommunityElements() {
    if (this.movie == null) return;

    if (this.featureFilmInteractionToken && this.filmInteractionComponent) {
      this.featureFilmInteractionTokenEnabled = true;

      if (this.filmInteractionComponentRef) {
        this.filmInteractionComponentRef.instance.movie = this.movie;
      } else {
        this.filmInteractionComponentRef = this.filmInteractionComponent.createComponent(
          FeatureFilmInteractionsComponent
        );

        this.filmInteractionComponentRef.instance.movie = this.movie;
      }
    }
  }

  onMovieFullyLoaded(movie: Movie) {
    this.competitionsService.getCompetition(movie.id).subscribe(competition => {
      this.competition = competition;
    });
    this.movieLaurels = this.getMovieLaurels(movie);
    this.rightOwners = movie.contractualPartners;
    this.hasUPlayer = movie.hasPlayer;
    if (this.hasUPlayer && this.isBrowser) this.uplayerService.retrieveAvailablePlayerPrices(movie.id);
    this.setInitialTracking(movie);
    this.setMovieTags(movie);
    this.setShowtimeStreamingPriority(movie);
    this.loadCommunityElements();
    this.cd.detectChanges();
  }

  onImgError(src) {
    if (src == 'landscape') {
      this.movieLandscapeImageBroken = true;
    } else {
      this.moviePortraitImageBroken = true;
    }
  }

  getMovieLaurels(movie: Movie): MovieLaurel[] {
    const participations: MovieLaurel[] = movie.movieFestivals?.map(festival => {
      const laurel: MovieLaurel = {
        festivalName: festival.festivalEdition.festival.name,
        festivalEdition: festival.festivalEdition.name,
        awardName: festival.festivalSection.name,
        position: festival.festivalSection.position,
        festivalLogo: festival.festivalEdition.festival.festivalLogo
      };

      return laurel;
    });

    // put both togheter and sort b position
    if (participations) {
      return participations.filter(laurel => laurel.position).sort((a, b) => a.position - b.position);
    }

    return null;
  }

  /**
   * Set the metatags for this page.
   */
  private setInitialTracking(movie: Movie) {
    this.trackingService.trackEvent({
      action: 'FilmPageView',
      properties: {
        category: 'MovieDetail',
        label: movie.title,
        movie: movie.title,
        idMovie: movie.id,
        imdb: movie?.imdb,
        tagsMovie: getMovieTags(movie),
        genresMovie: getMovieGenres(movie)
      }
    });
  }

  /**
   * Sets {@link movieTagClass}.
   */
  setMovieTags(movie: Movie) {
    this.noMovieDetailTag = existsMovieTag(movie, 'friendlyUrl', 'no-movie-detail');
    this.movieTagClass = movie?.tags?.map(t => t.friendlyUrl) || [];
  }

  /**
   * Use scrollIntoView to a match element.
   * @param elSelector
   * @param params
   */
  scrollTo(elSelector: string, params?: any) {
    // todo: test this
    // const el = document.querySelector(elSelector);
    const settings = typeof params == 'undefined' ? { behavior: 'smooth', block: 'center', inline: 'center' } : params;
    // el.scrollIntoView(settings);
  }

  /**
   * @deprecated Don't found any use
   */
  handleUplayLoaded() {
    this.scrollTo(this.uplaybtn);
  }

  /**
   * Check if there is some info to display the {@link MovieMoreInformationComponent}.
   *
   * //TODO barbeitoalex propostal - why not move this to {@link MovieMoreInformationComponent}?
   * @param movie
   * @returns
   */
  thereIsMoreInfo(movie: Movie): boolean {
    if (movie.certification) return true;
    if (movie.countries && movie.countries.length !== 0) return true;
    if (movie.languages && movie.languages.length !== 0) return true;
    if (movie.releaseDate) return true;
    if (movie.runtime) return true;
    if (movie.genres && movie?.genres.length > 0) return true;
    if (movie.portraitPhotoUrl) return true;
    return false;
  }

  /**
   * @docNotNeccesary
   */
  setVideoReady() {
    this.videoReady.next(true);
  }

  /**
   * //TODO docs favio
   * @returns
   */
  setShowtimeStreamingPriority(movie: Movie) {
    if (this.whereToWatch) {
      this.showtimeStreamingPriority = this.whereToWatch;
      return;
    }

    if (!this.env?.showtimeStreamingConfig) return;
    // check for movie first
    if (this.env.showtimeStreamingConfig?.priorityByMovie) {
      this.showtimeStreamingPriority = this.env.showtimeStreamingConfig.priorityByMovie.find(
        conf => conf.slug == movie.friendlyUrl
      )?.priority;
    } else {
      this.showtimeStreamingPriority = this.env.showtimeStreamingConfig?.priority;
    }
  }

  ngOnDestroy() {
    this.titleService.setFavicon();

    this.metaTagsService.resetMetaTags();
  }

  private setMetaTags(movie: Movie): void {
    const metaTags: MetaTags = {
      url: this.location.path(),

      title: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.title`)
        ? `movie.${movie.friendlyUrl}.title`
        : `meta.page.movie-detail.title`,

      description: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.description`)
        ? `movie.${movie.friendlyUrl}.description`
        : `meta.page.movie-detail.description`,

      video: movie.trTrailerLink
    };

    if (movie.tilePhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.tilePhotoUrl, { width: 500 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.portraitPhotoUrl, { height: 500 });
    }

    const director = movie?.movieCrew?.find(mc => mc.crewRole.name.toLowerCase() == 'director')?.star?.name;

    this.metaTagsService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
    this.titleService.setFavicon(movie.faviconImageUrl);
  }

  goToParamSection() {
    if (!this.isBrowser) return;
    const section = this.route.snapshot.queryParams['section'];
    if (!section) return;
    const id = section === 'in-cinemas' || section === 'watch-at-home' ? 'infoRight' : undefined;
    if (!id) return;
    // scroll with some offset
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => {
        const offset = 120;
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }, 400); // wait for the view to be rendered
    } else {
      console.warn(`Element with id ${id} not found.`);
    }
  }

  private setSwiperConfigs() {
    this.reviewsSwiperConfig = {
      slidesPerView: 1,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      spaceBetween: 10,
      scrollbar: false,
      navigation: true
    };
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
